import { useState, useMemo } from 'react'
import { Calculator, ChevronDown, ChevronUp } from 'lucide-react'
import { formatPriceWithCents } from '@/utils/format'

interface FinanceCalculatorProps {
  price: number
  /** Dealer-customisable default interest rate */
  defaultRate?: number
  /** Dealer branding (fallback when no provider set) */
  dealerName?: string
  dealerLogo?: string
  /** Finance provider details */
  providerName?: string
  providerLogo?: string
  providerUrl?: string
  description?: string
  contactEmail?: string
  contactPhone?: string
  minAmount?: number
  maxTermYears?: number
  termsUrl?: string
}

/**
 * Finance calculator for vehicle listings.
 * Shows estimated monthly repayments based on price, deposit, term, and interest rate.
 * Collapsible by default to keep the listing page clean.
 */
export function FinanceCalculator({
  price,
  defaultRate = 9.95,
  dealerName,
  dealerLogo,
  providerName,
  providerLogo,
  providerUrl,
  description,
  contactEmail,
  contactPhone,
  minAmount,
  maxTermYears,
  termsUrl,
}: FinanceCalculatorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [deposit, setDeposit] = useState(Math.round(price * 0.1))
  const maxYears = Math.min(Math.max(maxTermYears ?? 5, 1), 7) // clamp 1..7
  const termOptions = Array.from({ length: maxYears }, (_, i) => (i + 1) * 12)
  const initialTermMonths = Math.min(60, maxYears * 12)
  const [termMonths, setTermMonths] = useState(initialTermMonths)
  const [rate, setRate] = useState(defaultRate)
  const [frequency, setFrequency] = useState<'weekly' | 'fortnightly' | 'monthly'>('weekly')

  const loanAmount = Math.max(0, price - deposit)

  const monthlyPayment = useMemo(() => {
    if (loanAmount <= 0 || rate <= 0 || termMonths <= 0) return 0
    const monthlyRate = rate / 100 / 12
    const numerator = monthlyRate * Math.pow(1 + monthlyRate, termMonths)
    const denominator = Math.pow(1 + monthlyRate, termMonths) - 1
    return loanAmount * (numerator / denominator)
  }, [loanAmount, rate, termMonths])

  const displayPayment = useMemo(() => {
    if (frequency === 'weekly') return monthlyPayment * 12 / 52
    if (frequency === 'fortnightly') return monthlyPayment * 12 / 26
    return monthlyPayment
  }, [monthlyPayment, frequency])

  const frequencyLabel = frequency === 'weekly' ? 'week' : frequency === 'fortnightly' ? 'fortnight' : 'month'

  const totalRepayment = monthlyPayment * termMonths
  const totalInterest = totalRepayment - loanAmount

  return (
    <div className="card overflow-hidden">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary-600" />
          <span className="font-medium text-sm">Finance Calculator</span>
          {!isOpen && monthlyPayment > 0 && (
            <span className="text-xs text-slate-500 ml-1">
              est. {formatPriceWithCents(displayPayment)}/{frequency === 'weekly' ? 'wk' : frequency === 'fortnightly' ? 'fn' : 'mo'}
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {/* Calculator body */}
      {isOpen && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100">
          {/* Finance provider branding */}
          {(providerName || dealerName) && (
            <div className="flex items-center gap-3 pt-3">
              {(providerLogo || dealerLogo) && (
                <img
                  src={providerLogo || dealerLogo}
                  alt={providerName || dealerName || ''}
                  // Prominent provider branding (marketing for the finance partner).
                  // object-contain so logos are never cropped or distorted.
                  className="h-12 w-auto max-w-[160px] object-contain"
                />
              )}
              {providerName && providerUrl ? (
                <a href={providerUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:text-primary-700">
                  Finance by {providerName}
                </a>
              ) : (
                <span className="text-xs text-slate-500">
                  Finance {providerName ? `by ${providerName}` : dealerName ? `estimate by ${dealerName}` : 'calculator'}
                </span>
              )}
            </div>
          )}

          {description && (
            <p className="text-xs text-slate-500 leading-relaxed">{description}</p>
          )}

          {/* Minimum amount note */}
          {minAmount && price < minAmount && (
            <div className="pt-2 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1.5">
              Minimum finance amount: ${minAmount.toLocaleString()}
            </div>
          )}

          {/* Payment display */}
          <div className="text-center py-3">
            <p className="text-3xl font-bold text-primary-600">
              {formatPriceWithCents(displayPayment)}
            </p>
            <p className="text-sm text-slate-500">per {frequencyLabel}</p>
          </div>

          {/* Frequency toggle */}
          <div className="grid grid-cols-3 gap-1.5">
            {(['weekly', 'fortnightly', 'monthly'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFrequency(f)}
                className={`py-1.5 rounded text-xs font-medium transition-colors ${
                  frequency === f
                    ? 'bg-primary-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {f === 'weekly' ? 'Weekly' : f === 'fortnightly' ? 'Fortnightly' : 'Monthly'}
              </button>
            ))}
          </div>

          {/* Inputs */}
          <div className="space-y-3">
            {/* Vehicle price (read-only) */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Vehicle Price</span>
              <span className="font-medium">{formatPriceWithCents(price)}</span>
            </div>

            {/* Deposit */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="finance-deposit" className="text-sm text-slate-600">Deposit</label>
                <span className="text-sm font-medium">{formatPriceWithCents(deposit)}</span>
              </div>
              <input
                id="finance-deposit"
                type="range"
                min={0}
                max={Math.round(price * 0.9)}
                step={100}
                value={deposit}
                onChange={(e) => setDeposit(Number(e.target.value))}
                className="w-full accent-primary-500"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>$0</span>
                <span>{Math.round((deposit / price) * 100)}%</span>
                <span>{formatPriceWithCents(price * 0.9)}</span>
              </div>
            </div>

            {/* Loan term */}
            <div>
              <label className="text-sm text-slate-600 block mb-1">Loan Term</label>
              <div className="flex flex-wrap gap-1.5">
                {termOptions.map((months) => (
                  <button
                    key={months}
                    type="button"
                    onClick={() => setTermMonths(months)}
                    className={`flex-1 min-w-[60px] py-1.5 rounded text-xs font-medium transition-colors ${
                      termMonths === months
                        ? 'bg-primary-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {months / 12}yr
                  </button>
                ))}
              </div>
            </div>

            {/* Interest rate */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="finance-rate" className="text-sm text-slate-600">Interest Rate</label>
                <span className="text-sm font-medium">{rate.toFixed(2)}%</span>
              </div>
              <input
                id="finance-rate"
                type="range"
                min={1}
                max={25}
                step={0.25}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="w-full accent-primary-500"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Loan Amount</span>
              <span>{formatPriceWithCents(loanAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Total Interest</span>
              <span>{formatPriceWithCents(totalInterest)}</span>
            </div>
            <div className="flex justify-between font-medium pt-1.5 border-t border-slate-200">
              <span>Total Repayment</span>
              <span>{formatPriceWithCents(totalRepayment)}</span>
            </div>
          </div>

          {/* Apply button — links out to the finance provider's website */}
          {providerUrl && (
            <a
              href={providerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-colors"
            >
              Apply now{providerName ? ` with ${providerName}` : ''}
            </a>
          )}

          {/* Provider footer */}
          {(contactEmail || contactPhone || termsUrl) && (
            <div className="text-xs text-slate-500 space-y-1 pt-1">
              {(contactEmail || contactPhone) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  {contactEmail && (
                    <a href={`mailto:${contactEmail}`} className="text-primary-600 hover:text-primary-700">{contactEmail}</a>
                  )}
                  {contactPhone && (
                    <a href={`tel:${contactPhone}`} className="text-primary-600 hover:text-primary-700">{contactPhone}</a>
                  )}
                </div>
              )}
              {termsUrl && (
                <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                  Terms & conditions apply
                </a>
              )}
            </div>
          )}

          {/* Disclaimer */}
          <p className="text-[10px] text-slate-400 leading-tight">
            This calculator provides estimates only and does not constitute a finance offer.
            Actual rates and terms depend on your individual circumstances and the finance provider.
            {!contactEmail && !contactPhone && ' Contact a finance provider for a personalised quote.'}
          </p>
        </div>
      )}
    </div>
  )
}
